'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { PeopleSection, Person } from '@/types/sanity';
import { SectionHeader } from './SectionHeader';
import { SectionCTAs } from './SectionCTAs';
import Image from 'next/image';
import Link from 'next/link';
import {
  ScrollReveal,
  StaggerContainer,
  staggerItemVariants,
} from '@/components/animations/ScrollAnimations';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { urlFor } from '@/sanity/lib/image';
import { cn } from '@/lib/utils';

interface PeopleSectionComponentProps {
  section: PeopleSection;
  pageSlug?: string;
}

function PersonCard({ person }: { person: Person }) {
  const imageUrl = person.image?.asset
    ? urlFor(person.image).width(400).height(400).quality(80).url()
    : null;
  const hasBioContent = person.bio || person.quote;

  return (
    <div
      className={cn(
        'flex flex-col items-center text-center',
        hasBioContent && 'md:flex-row md:items-start md:text-left md:gap-6'
      )}
    >
      <div className={cn('shrink-0', hasBioContent && 'md:w-40')}>
        {imageUrl ? (
          <div className="relative mx-auto mb-4 h-40 w-40 overflow-hidden rounded-full md:mb-0">
            <Image
              src={imageUrl}
              alt={person.image?.alt || person.name}
              fill
              className="object-cover"
              sizes="160px"
            />
          </div>
        ) : (
          <div className="mx-auto mb-4 flex h-40 w-40 items-center justify-center rounded-full bg-muted md:mb-0">
            <span className="text-4xl text-muted-foreground">{person.name.charAt(0)}</span>
          </div>
        )}
      </div>
      <div className="min-w-0">
        <h3 className="font-medium text-lg">{person.name}</h3>
        {person.role && <p className="text-muted-foreground text-sm mt-1">{person.role}</p>}
        {person.quote && (
          <p className="text-muted-foreground text-sm mt-3 italic">&ldquo;{person.quote}&rdquo;</p>
        )}
        {person.bio && (
          <div className="prose prose-sm max-w-none mt-3 text-muted-foreground">
            <PortableText value={person.bio} />
          </div>
        )}
        {person.linkedIn && (
          <Link
            href={person.linkedIn}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-sm text-primary hover:underline"
          >
            LinkedIn
          </Link>
        )}
      </div>
    </div>
  );
}

export function PeopleSectionComponent({ section, pageSlug }: PeopleSectionComponentProps) {
  const layout = section.layout || 'grid-3';
  const hasBioContent = section.people?.some((p) => p.bio || p.quote);

  const getGridClass = () => {
    if (hasBioContent) {
      // Horizontal cards need more width — cap at 2 columns
      return 'grid grid-cols-1 lg:grid-cols-2 gap-8';
    }
    switch (layout) {
      case 'grid-2':
        return 'grid grid-cols-2 sm:grid-cols-2 gap-6';
      case 'grid-3':
        return 'grid grid-cols-2 sm:grid-cols-3 gap-6';
      case 'grid-4':
        return 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6';
      default:
        return 'grid grid-cols-2 sm:grid-cols-3 gap-6';
    }
  };

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'people-section py-8 md:py-12 lg:py-16',
          pageSlug && `people-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <SectionHeader
              title={section.title}
              subtitle={section.subtitle}
              description={section.description}
            />

            {layout === 'carousel' ? (
              <Carousel opts={{ align: 'start', loop: true }} className="w-full">
                <CarouselContent className="-ml-4">
                  {section.people?.map((person) => (
                    <CarouselItem
                      key={person._id}
                      className={cn(
                        'pl-4',
                        hasBioContent ? 'basis-full lg:basis-1/2' : 'basis-1/2 sm:basis-1/3'
                      )}
                    >
                      <PersonCard person={person} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            ) : (
              <StaggerContainer staggerDelay={0.15} className={getGridClass()}>
                {section.people?.map((person) => (
                  <motion.div key={person._id} variants={staggerItemVariants}>
                    <PersonCard person={person} />
                  </motion.div>
                ))}
              </StaggerContainer>
            )}

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
